import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Search, Loader2, Send, Sparkles, ExternalLink, Zap, Brain, Trash2, History, Share2, BookOpen, Landmark } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function Research() {
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("deep");
  const [busy, setBusy] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [streamingId, setStreamingId] = useState(null); // id of message currently typewriter-rendering
  const [streamedText, setStreamedText] = useState("");
  const [shareOpen, setShareOpen] = useState(null); // { messageId, content, citations }
  const scrollRef = useRef(null);
  const streamTimerRef = useRef(null);

  const loadHistory = async () => {
    const r = await api.get("/research/conversations");
    setConversations(r.data);
  };
  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const openConversation = async (cid) => {
    setConvId(cid);
    const r = await api.get(`/research/conversations/${cid}/messages`);
    setMessages(r.data);
  };

  const newConversation = () => { setConvId(null); setMessages([]); };

  const submit = async (queryOverride) => {
    const query = (queryOverride ?? input).trim();
    if (!query || busy) return;
    setInput("");
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: query, created_at: new Date().toISOString() };
    setMessages((m) => [...m, tempUser]);
    setBusy(true);
    try {
      const r = await api.post("/research/search", { query, mode, conversation_id: convId });
      setConvId(r.data.conversation_id);
      const assistant = r.data.message;
      // Insert with empty content, then typewriter-reveal
      setMessages((m) => [...m, { ...assistant, content: "" }]);
      setBusy(false);
      streamReveal(assistant);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Research failed");
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
      setBusy(false);
    }
  };

  // Typewriter streaming effect — reveals 24 chars / 12ms tick for a Copilot-like feel
  const streamReveal = (message) => {
    const full = message.content || "";
    setStreamingId(message.id);
    setStreamedText("");
    let i = 0;
    const chunk = 24;
    const interval = setInterval(() => {
      i += chunk;
      const slice = full.slice(0, i);
      setStreamedText(slice);
      setMessages((arr) => arr.map((m) => (m.id === message.id ? { ...m, content: slice } : m)));
      if (i >= full.length) {
        clearInterval(interval);
        setMessages((arr) => arr.map((m) => (m.id === message.id ? { ...m, content: full, citations: message.citations, queries_used: message.queries_used } : m)));
        setStreamingId(null);
        streamTimerRef.current = null;
      }
    }, 12);
    streamTimerRef.current = { interval, message, full };
  };

  const skipStream = () => {
    const s = streamTimerRef.current;
    if (!s) return;
    clearInterval(s.interval);
    setMessages((arr) => arr.map((m) => (m.id === s.message.id ? { ...m, content: s.full, citations: s.message.citations, queries_used: s.message.queries_used } : m)));
    setStreamingId(null);
    streamTimerRef.current = null;
  };

  const shareToTeam = async (destination) => {
    if (!shareOpen) return;
    try {
      const r = await api.post(`/research/messages/${shareOpen.messageId}/share`, {
        destination,
        title: shareOpen.title || "",
      });
      toast.success(destination === "knowledge" ? "Saved to Intelligence library" : "Shared as briefing");
      setShareOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share");
    }
  };

  const deleteConv = async (cid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this research thread?")) return;
    await api.delete(`/research/conversations/${cid}`);
    if (cid === convId) newConversation();
    loadHistory();
  };

  const SUGGESTED = [
    "What are Ghana's top 5 institutional risks for 2026?",
    "Compare PPP frameworks across Ghana, Kenya, and Rwanda.",
    "Latest sovereign wealth fund allocations to West African ag-tech.",
    "Summarize the African Continental Free Trade Area (AfCFTA) progress in 2026.",
  ];

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="research-page">
      {/* History sidebar */}
      <aside className="w-[280px] shrink-0 border-r border-white/5 flex flex-col" style={{ background: "var(--bg-surface-1)" }}>
        <div className="p-4 border-b border-white/5">
          <button
            onClick={newConversation}
            data-testid="new-research-btn"
            className="w-full px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            <Sparkles className="w-3.5 h-3.5" /> New research
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="label-eyebrow px-2 mb-2 flex items-center gap-2"><History className="w-3 h-3" /> History</div>
          {conversations.map((c) => (
            <div
              key={c.id}
              onClick={() => openConversation(c.id)}
              className={`group p-2 cursor-pointer flex items-start justify-between ${c.id === convId ? "bg-[#17223B]" : "hover:bg-[#10182B]"}`}
              data-testid={`history-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">{c.title}</div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{c.mode?.toUpperCase()} · {new Date(c.created_at).toLocaleDateString()}</div>
              </div>
              <button onClick={(e) => deleteConv(c.id, e)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-red-400">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          {conversations.length === 0 && (
            <div className="p-3 text-[11px] text-slate-600 text-center">No research yet</div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="px-8 py-4 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">// Nexus Research</div>
            <h1 className="font-display text-2xl text-white mt-1">Deep web intelligence, with citations.</h1>
          </div>
          <div className="flex items-center gap-2 border border-white/10">
            {[
              { id: "quick", label: "Quick", icon: Zap },
              { id: "deep", label: "Deep", icon: Brain },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                data-testid={`mode-${m.id}`}
                className={`px-4 py-2 text-xs font-mono tracking-widest flex items-center gap-1.5 ${mode === m.id ? "bg-[#D4AF37] text-[#050B14]" : "text-slate-400 hover:text-white"}`}
              >
                <m.icon className="w-3 h-3" /> {m.label.toUpperCase()}
              </button>
            ))}
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-6">
          {messages.length === 0 && !busy && (
            <div className="max-w-3xl mx-auto py-12">
              <div className="text-center mb-10">
                <Search className="w-10 h-10 mx-auto mb-4 text-[#D4AF37]" strokeWidth={1} />
                <h2 className="font-display text-3xl text-white">Ask anything. Get cited intelligence.</h2>
                <p className="text-slate-400 mt-3 max-w-xl mx-auto">
                  Nexus Research combines live web search with executive-grade synthesis. Every claim is cited to its source.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    onClick={() => submit(s)}
                    className="text-left p-4 border border-white/10 hover:border-[#D4AF37]/40 text-sm text-slate-300 hover:text-white transition-colors"
                    data-testid={`suggested-${s.slice(0, 20)}`}
                  >
                    <ExternalLink className="w-3 h-3 inline mr-2 text-[#D4AF37]" /> {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <div key={m.id} className={`max-w-3xl mx-auto mb-6 ${m.role === "user" ? "" : ""}`}>
              {m.role === "user" ? (
                <div className="flex justify-end" data-testid="user-msg">
                  <div className="max-w-[80%] px-5 py-3 nexus-glass rounded-2xl rounded-tr-sm text-[14px] text-white">
                    {m.content}
                  </div>
                </div>
              ) : (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 group" data-testid="assistant-msg">
                  <div className="nexus-prose max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ""}</ReactMarkdown>
                    {streamingId === m.id && (
                      <>
                        <span className="nexus-caret" />
                        <button
                          onClick={skipStream}
                          data-testid="skip-stream-btn"
                          className="ml-3 nexus-pill bg-white/[0.04] hover:bg-white/[0.08] text-slate-400 border border-white/10 text-[10px]"
                          title="Show full response immediately"
                        >
                          Skip animation
                        </button>
                      </>
                    )}
                  </div>
                  {m.citations && m.citations.length > 0 && streamingId !== m.id && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <div className="label-eyebrow mb-3">// Sources ({m.citations.length})</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {m.citations.map((c) => (
                          <a key={c.n} href={c.url} target="_blank" rel="noreferrer"
                            className="block p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/5 transition-all group/cite" data-testid={`citation-${c.n}`}>
                            <div className="flex items-start gap-2">
                              <span className="font-mono text-[10px] text-[#00F0FF] mt-0.5 shrink-0 px-1.5 py-0.5 rounded bg-[#00F0FF]/10">[{c.n}]</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] text-white truncate group-hover/cite:text-[#FCE588]">{c.title}</div>
                                <div className="text-[10px] text-slate-500 font-mono truncate mt-0.5">{(c.url || "").replace(/^https?:\/\//, "")}</div>
                              </div>
                              <ExternalLink className="w-3 h-3 text-slate-600 group-hover/cite:text-[#00F0FF] mt-1 shrink-0" />
                            </div>
                          </a>
                        ))}
                      </div>
                      <div className="mt-4 flex items-center gap-2">
                        <button
                          onClick={() => setShareOpen({ messageId: m.id, content: m.content, citations: m.citations })}
                          data-testid={`share-btn-${m.id}`}
                          className="nexus-pill bg-white/[0.04] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/40 text-[#FCE588] border border-white/10"
                        >
                          <Share2 className="w-3 h-3" /> Share with team
                        </button>
                        <button
                          onClick={() => { navigator.clipboard.writeText(m.content); toast.success("Copied to clipboard"); }}
                          className="nexus-pill bg-white/[0.04] hover:bg-white/[0.08] text-slate-400 border border-white/10"
                        >
                          Copy briefing
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          ))}

          {busy && (
            <div className="max-w-3xl mx-auto mb-6 flex items-center gap-3 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" />
              <span className="text-sm">
                {mode === "deep" ? "Running multi-query deep research…" : "Searching the web…"}
              </span>
            </div>
          )}
        </div>

        <div className="px-8 py-5 border-t border-white/5">
          <div className="max-w-3xl mx-auto flex items-end gap-3 nexus-glass-panel rounded-2xl p-2 focus-within:border-[#D4AF37]/40 focus-within:shadow-[0_0_30px_rgba(212,175,55,0.12)] transition-all">
            <textarea
              data-testid="research-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={1}
              placeholder="Ask Nexus Research anything — policy, markets, competitors, sovereign trends…"
              className="flex-1 resize-none px-4 py-3 bg-transparent outline-none text-[14px] text-white placeholder:text-slate-600 max-h-32"
            />
            <button
              onClick={() => submit()}
              disabled={busy || !input.trim()}
              data-testid="research-submit"
              className="px-4 py-3 rounded-xl font-medium text-sm disabled:opacity-40 flex items-center gap-2 transition-all hover:scale-105"
              style={{ background: "linear-gradient(135deg, #FCE588 0%, #D4AF37 100%)", color: "#050B14" }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          <div className="max-w-3xl mx-auto mt-3 text-[10px] text-slate-600 font-mono tracking-widest text-center">
            POWERED BY TAVILY DEEP SEARCH + CLAUDE SONNET 4.5 · ENTER TO SEND · SHIFT + ENTER FOR NEW LINE
          </div>
        </div>
      </div>

      {/* Share modal */}
      {shareOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShareOpen(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="nexus-glass-panel p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="share-modal"
          >
            <div className="label-eyebrow mb-2">// Share Research</div>
            <h2 className="font-display text-2xl text-white mb-1">Convert to institutional memory</h2>
            <p className="text-slate-400 text-sm mb-6">Where would you like to file this briefing?</p>
            <div className="space-y-2.5">
              <button
                onClick={() => shareToTeam("knowledge")}
                data-testid="share-knowledge"
                className="w-full text-left p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5 transition-all flex items-start gap-3 group"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border border-white/5 bg-white/5 group-hover:border-[#D4AF37]/30 transition-all">
                  <BookOpen className="w-4 h-4 text-[#FCE588]" strokeWidth={1.6} />
                </div>
                <div className="flex-1">
                  <div className="text-[14px] text-white font-medium">Save to Intelligence library</div>
                  <div className="text-[11px] text-slate-500 mt-1">Adds a tagged Knowledge document with all sources. Searchable team-wide.</div>
                </div>
              </button>
              <button
                onClick={() => shareToTeam("briefing")}
                data-testid="share-briefing"
                className="w-full text-left p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#00F0FF]/40 hover:bg-[#00F0FF]/5 transition-all flex items-start gap-3 group opacity-60 cursor-not-allowed"
                disabled
                title="Pick a stakeholder from the Engagement Hub first"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border border-white/5 bg-white/5">
                  <Landmark className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.6} />
                </div>
                <div className="flex-1">
                  <div className="text-[14px] text-white font-medium">Post as Engagement Hub briefing</div>
                  <div className="text-[11px] text-slate-500 mt-1">Open a stakeholder from Engagement Hub to file as a briefing there.</div>
                </div>
              </button>
            </div>
            <button onClick={() => setShareOpen(null)} className="mt-5 w-full py-2 text-xs font-mono tracking-widest text-slate-500 hover:text-white transition-colors">
              CANCEL
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
